var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@radix-ui/react-icons";
import { Dialog } from "radix-ui";
import { useMarketing, useUI } from "../../providers";
import { DealsSelection, setPendingDealCode } from "../dealsSelection";
import { ItemCustomizationDialog } from "../itemCustomization";
import { RadixDialogue } from "../primitives/RadixDialogue";
import GoogleAnalytics from "../../analytics/google";
import { shouldDisplayMenuEntity } from "../utility/menuItemVisibility";
import "./marketingDisplay.css";
var DEFAULT_SLIDE_DURATION_MS = 3500;
var DISMISSAL_STORAGE_PREFIX = "marketing-display-dismissed";
var isPositiveNumber = function (value) {
    return typeof value === "number" && Number.isFinite(value) && value > 0;
};
var getPrimaryAssetUrl = function (assetUrl) {
    if (Array.isArray(assetUrl)) {
        var firstUrl = assetUrl.find(function (url) { return typeof url === "string" && url.trim().length > 0; });
        return typeof firstUrl === "string" ? firstUrl : null;
    }
    if (typeof assetUrl === "string" && assetUrl.trim().length > 0) {
        return assetUrl;
    }
    return null;
};
var getSessionCategories = function () {
    var _a;
    var categories = (_a = window.session) === null || _a === void 0 ? void 0 : _a.categories;
    return Array.isArray(categories) ? categories : [];
};
var findItemNavigationByMenuId = function (menuId) {
    if (typeof menuId !== "number" && typeof menuId !== "string") {
        return null;
    }
    var sessionCategories = getSessionCategories();
    var menuIdAsString = String(menuId);
    for (var _i = 0, sessionCategories_1 = sessionCategories; _i < sessionCategories_1.length; _i++) {
        var category = sessionCategories_1[_i];
        var categoryId = category === null || category === void 0 ? void 0 : category.id;
        if ((typeof categoryId !== "number" && typeof categoryId !== "string") || !Array.isArray(category.item_navigations)) {
            continue;
        }
        for (var _a = 0, _b = category.item_navigations; _a < _b.length; _a++) {
            var itemNavigation = _b[_a];
            if (!shouldDisplayMenuEntity(itemNavigation)) {
                continue;
            }
            var itemMenuId = itemNavigation === null || itemNavigation === void 0 ? void 0 : itemNavigation.menu_id;
            var itemNavigationId = itemNavigation === null || itemNavigation === void 0 ? void 0 : itemNavigation.id;
            if ((typeof itemMenuId !== "number" && typeof itemMenuId !== "string") || (typeof itemNavigationId !== "number" && typeof itemNavigationId !== "string")) {
                continue;
            }
            if (String(itemMenuId) === menuIdAsString) {
                return {
                    categoryId: String(categoryId),
                    itemNavigationId: String(itemNavigationId),
                    itemName: typeof itemNavigation.name === "string" ? itemNavigation.name : "Customize item",
                };
            }
        }
    }
    return null;
};
var getDismissalStorage = function () {
    if (typeof window === "undefined") {
        return null;
    }
    try {
        if (window.localStorage) {
            return window.localStorage;
        }
    }
    catch (_error) {
        // no-op; fallback to sessionStorage
    }
    try {
        if (window.sessionStorage) {
            return window.sessionStorage;
        }
    }
    catch (_error) {
        // no-op; storage unavailable
    }
    return null;
};
export var MarketingDisplay = function (_a) {
    var placement = _a.placement;
    var marketingData = useMarketing().marketingData;
    var showToast = useUI().showToast;
    var _b = useState(false), isOpen = _b[0], setIsOpen = _b[1];
    var _c = useState(false), isDealsFlyOutOpen = _c[0], setIsDealsFlyOutOpen = _c[1];
    var _d = useState({
        open: false,
        itemUrl: null,
        title: "Customize item",
    }), itemDialogState = _d[0], setItemDialogState = _d[1];
    var _e = useState(0), activeSlideIndex = _e[0], setActiveSlideIndex = _e[1];
    var carouselItems = useMemo(function () {
        var marketingDisplayItems = Array.isArray(marketingData.marketingDisplayItems)
            ? marketingData.marketingDisplayItems
            : [];
        return marketingDisplayItems
            .filter(function (item) {
            if (placement === "home") {
                return Boolean(item.displayAtHomeScreen);
            }
            return Boolean(item.displayAtCheckout);
        })
            .map(function (item) {
            var assetUrl = getPrimaryAssetUrl(item.assetUrl);
            var width = isPositiveNumber(item.width) ? item.width : null;
            var height = isPositiveNumber(item.height) ? item.height : null;
            var hasValidDimensions = width !== null && height !== null;
            return {
                assetUrl: assetUrl,
                width: width,
                height: height,
                hasValidDimensions: hasValidDimensions,
                displayTitle: typeof item.displayTitle === "string" ? item.displayTitle : "",
                displayText: typeof item.displayText === "string" ? item.displayText : "",
                displayCtaButton: Boolean(item.displayCtaButton),
                ctaButtonText: typeof item.ctaButtonText === "string" && item.ctaButtonText.trim().length > 0
                    ? item.ctaButtonText
                    : "Learn More",
                ctaType: typeof item.ctaType === "string" ? item.ctaType.toLowerCase() : "",
                ctaItem: typeof item.ctaItem === "number" || typeof item.ctaItem === "string" ? item.ctaItem : null,
                ctaUrl: typeof item.ctaUrl === "string" && item.ctaUrl.trim().length > 0
                    ? item.ctaUrl
                    : null,
                duration: isPositiveNumber(item.duration) ? item.duration : null,
                priority: isPositiveNumber(item.priority) ? item.priority : Number.MAX_SAFE_INTEGER,
                promotionCode: typeof item.promotionCode === "string" ? item.promotionCode.trim() : "",
            };
        })
            .filter(function (item) { return Boolean(item.assetUrl); })
            .sort(function (itemA, itemB) { return itemA.priority - itemB.priority; });
    }, [marketingData.marketingDisplayItems, placement]);
    var slideCount = carouselItems.length;
    var activeItem = slideCount > 0 ? carouselItems[activeSlideIndex] : null;
    var storageKey = useMemo(function () {
        if (slideCount === 0) {
            return null;
        }
        var signature = carouselItems
            .map(function (item) { return "".concat(item.assetUrl, "|").concat(item.displayTitle, "|").concat(item.displayText, "|").concat(item.priority); })
            .join("||");
        return "".concat(DISMISSAL_STORAGE_PREFIX, ":").concat(placement, ":").concat(signature);
    }, [carouselItems, placement, slideCount]);
    var markDismissed = useCallback(function () {
        if (!storageKey) {
            return;
        }
        var storage = getDismissalStorage();
        if (!storage) {
            return;
        }
        storage.setItem(storageKey, "1");
    }, [storageKey]);
    useEffect(function () {
        if (slideCount === 0) {
            setIsOpen(false);
            return;
        }
        if (!storageKey) {
            setIsOpen(true);
            return;
        }
        var storage = getDismissalStorage();
        var isDismissed = storage ? storage.getItem(storageKey) === "1" : false;
        setIsOpen(!isDismissed);
    }, [slideCount, storageKey]);
    useEffect(function () {
        setActiveSlideIndex(0);
    }, [placement, slideCount]);
    useEffect(function () {
        var _a;
        if (slideCount <= 1 || !isOpen || !activeItem) {
            return;
        }
        var activeDuration = (_a = activeItem.duration) !== null && _a !== void 0 ? _a : DEFAULT_SLIDE_DURATION_MS;
        var intervalId = window.setInterval(function () {
            setActiveSlideIndex(function (previousIndex) { return (previousIndex + 1) % slideCount; });
        }, activeDuration);
        return function () {
            window.clearInterval(intervalId);
        };
    }, [activeItem, isOpen, slideCount]);
    var goToNext = useCallback(function () {
        setActiveSlideIndex(function (previousIndex) {
            if (slideCount === 0) {
                return 0;
            }
            return (previousIndex + 1) % slideCount;
        });
    }, [slideCount]);
    var goToPrevious = useCallback(function () {
        setActiveSlideIndex(function (previousIndex) {
            if (slideCount === 0) {
                return 0;
            }
            return (previousIndex - 1 + slideCount) % slideCount;
        });
    }, [slideCount]);
    var handleCtaClick = useCallback(function () {
        if (!activeItem) {
            return;
        }
        if (activeItem.ctaType === "order") {
            markDismissed();
            setIsOpen(false);
            return;
        }
        if (activeItem.ctaType === "deals") {
            markDismissed();
            setIsOpen(false);
            setIsDealsFlyOutOpen(true);
            return;
        }
        if (activeItem.ctaType === "category") {
            var match = findItemNavigationByMenuId(activeItem.ctaItem);
            if (!match) {
                return;
            }
            GoogleAnalytics.viewCategory(match.categoryId);
            var hash = "#category-".concat(match.categoryId);
            window.location.hash = hash;
            var categorySection = document.querySelector(".category-navigation-section[data-category-id=\"".concat(match.categoryId, "\"]"));
            if (categorySection instanceof HTMLElement) {
                categorySection.scrollIntoView({ behavior: "smooth", block: "start" });
            }
            markDismissed();
            setIsOpen(false);
            return;
        }
        if (activeItem.ctaType === "item") {
            var match = findItemNavigationByMenuId(activeItem.ctaItem);
            if (!match) {
                return;
            }
            GoogleAnalytics.viewItem({
                id: match.itemNavigationId,
                name: match.itemName,
                category: match.categoryId,
            });
            markDismissed();
            setIsOpen(false);
            setItemDialogState({
                open: true,
                itemUrl: "".concat(window.relativeURI(), "/menu/categories/").concat(match.categoryId, "/items/").concat(match.itemNavigationId),
                title: match.itemName,
            });
            return;
        }
        if (activeItem.ctaType === "accountsignup") {
            markDismissed();
            setIsOpen(false);
            window.location.assign("".concat(window.relativeURI(), "/user/signup"));
            return;
        }
        if (activeItem.ctaType === "promotion") {
            if (!activeItem.promotionCode) {
                return;
            }
            setPendingDealCode(activeItem.promotionCode);
            markDismissed();
            setIsOpen(false);
            return;
        }
        if (activeItem.ctaUrl) {
            markDismissed();
            window.open(activeItem.ctaUrl, "_blank", "noopener,noreferrer");
        }
    }, [activeItem, markDismissed]);
    if (!activeItem) {
        return null;
    }
    var imageWrapperStyle = activeItem.hasValidDimensions
        ? {
            aspectRatio: "".concat(activeItem.width, " / ").concat(activeItem.height),
        }
        : undefined;
    return (React.createElement(React.Fragment, null,
        React.createElement(RadixDialogue, { open: isOpen, onOpenChange: function (open) {
                if (!open) {
                    markDismissed();
                }
                setIsOpen(open);
            }, overlayClassName: "radix-modal-overlay", contentClassName: "marketing-display-dialog", useGeneratedDescriptionId: Boolean(activeItem.displayText) },
            React.createElement("div", { className: "marketing-display-dialog__header" },
                React.createElement("div", { className: "marketing-display-dialog__heading-block" },
                    React.createElement(Dialog.Title, { className: "marketing-display-dialog__title" }, activeItem.displayTitle || "Featured Promotion"),
                    activeItem.displayText ? (React.createElement(Dialog.Description, { className: "marketing-display-dialog__description" }, activeItem.displayText)) : null),
                React.createElement("div", { className: "marketing-display-dialog__actions" },
                    React.createElement(Dialog.Close, { className: "radix-flyout-close", "aria-label": "Close marketing promotion dialogue." },
                        React.createElement("span", { "aria-hidden": "true" }, "\u00D7")))),
            React.createElement("div", { className: "marketing-display-carousel__asset-region" },
                React.createElement("div", { className: "marketing-display-carousel__asset-wrapper", style: imageWrapperStyle },
                    React.createElement("img", { src: activeItem.assetUrl || "", alt: activeItem.displayTitle || "Featured promotion", className: "marketing-display-carousel__asset", loading: "lazy" }),
                    slideCount > 1 ? (React.createElement(React.Fragment, null,
                        React.createElement("button", { type: "button", className: "marketing-display-carousel__chevron marketing-display-carousel__chevron--left", onClick: goToPrevious, "aria-label": "Show previous promotion" },
                            React.createElement(ChevronLeftIcon, null)),
                        React.createElement("button", { type: "button", className: "marketing-display-carousel__chevron marketing-display-carousel__chevron--right", onClick: goToNext, "aria-label": "Show next promotion" },
                            React.createElement(ChevronRightIcon, null)))) : null)),
            (slideCount > 1 || (activeItem.displayCtaButton && (activeItem.ctaType || activeItem.ctaUrl))) ? (React.createElement("div", { className: "marketing-display-carousel__footer-controls" },
                slideCount > 1 ? (React.createElement("div", { className: "marketing-display-carousel__dots", "aria-label": "Promotion slides" }, carouselItems.map(function (_item, index) { return (React.createElement("button", { key: "marketing-slide-".concat(index), type: "button", className: [
                        "marketing-display-carousel__dot",
                        activeSlideIndex === index ? "marketing-display-carousel__dot--active" : "",
                    ].filter(Boolean).join(" "), onClick: function () { return setActiveSlideIndex(index); }, "aria-label": "Show promotion slide ".concat(index + 1), "aria-current": activeSlideIndex === index })); }))) : React.createElement("div", { className: "marketing-display-carousel__dots-spacer" }),
                activeItem.displayCtaButton && (activeItem.ctaType || activeItem.ctaUrl) ? (React.createElement("button", { type: "button", className: "marketing-display-dialog__cta", onClick: handleCtaClick }, activeItem.ctaButtonText)) : null)) : null),
        React.createElement(DealsSelection, { deals: window.session.deals || [], open: isDealsFlyOutOpen, onOpenChange: setIsDealsFlyOutOpen, hideHeader: true, presentation: "flyout", onPendingDealApply: function (deal) {
                showToast("Code ".concat(deal.code, " will be applied when you checkout."), "success");
            }, onSelectionComplete: function () {
                setIsDealsFlyOutOpen(false);
            } }),
        React.createElement(ItemCustomizationDialog, { open: itemDialogState.open, onOpenChange: function (open) {
                setItemDialogState(function (previousState) { return (__assign(__assign({}, previousState), { open: open, itemUrl: open ? previousState.itemUrl : null })); });
            }, itemUrl: itemDialogState.itemUrl, title: itemDialogState.title })));
};
